
// Constants for Field Dimensions (Percentages 0-100)
const FIELD_CENTER = { x: 50, y: 50 };
const PITCH_TOP = 40;
const PITCH_BOTTOM = 60;
const INNER_CIRCLE_RADIUS = 30; // 30-yard circle approx
const CLOSE_CATCHING_RADIUS = 15; // Close in fielders

/**
 * Calculates the standard cricket fielding position name based on coordinates.
 * @param {number} x - percentage (0-100) from left
 * @param {number} y - percentage (0-100) from top
 * @param {boolean} isRightHandedBat - context for Off/Leg side logic
 * @returns {string} - Position Name (e.g., "Deep Point")
 */
export const calculateFieldPositionName = (x, y, isRightHandedBat = true) => {
    // 1. Calculate Polar Coordinates relative to the Striker's End (Roughly Center-Bottom of pitch for this view)
    // Actually, standard field views usually center the oval. Let's assume (50, 50) is center of pitch.

    const dx = x - FIELD_CENTER.x;
    const dy = y - FIELD_CENTER.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Angle in degrees (0 is 3 o'clock, 90 is 6 o'clock)
    // We adjust so 0 is Straight Up (12 o'clock) for easier mental mapping? 
    // Let's use standard atan2: 0 is Right, -90 is Up, 90 is Down, 180 is Left.
    let angle = Math.atan2(dy, dx) * (180 / Math.PI);

    // Normalize to 0-360 starting from 12 o'clock (Top) moving Clockwise
    // atan2: Right(0), Down(90), Left(180), Up(-90)
    // Converted: Up(0), Right(90), Down(180), Left(270)
    let compassAngle = (angle + 90);
    if (compassAngle < 0) compassAngle += 360;

    // 2. Determine Depth Zone
    let prefix = '';
    if (distance < CLOSE_CATCHING_RADIUS) {
        prefix = 'Silly / Short';
    } else if (distance > INNER_CIRCLE_RADIUS + 5) { // Adding bit of buffer
        prefix = 'Deep';
    }

    // 3. Determine Sector Name (Assuming Right Hand Bat first)
    // 0 is Top (Bowler's End/Straight Hit), 180 is Bottom (Wicketkeeper)
    // Sectors for RHB:
    // 0-20: Straight Hit / Long Off
    // 340-360: Long On

    // Let's simplify by defining Off/Leg sides
    // RHB: Off Side is 180 -> 360 (Left half of screen?), Leg Side is 0 -> 180 (Right half)?
    // Wait, typical Top-Down view:
    // Top (0): Bowler run up.
    // Bottom (180): Keeper.
    // Right (90): Point/Cover (Off Side for RHB).
    // Left (270): Square Leg (Leg Side for RHB).

    let baseName = "Fielder";

    // Map angle to sectors
    if (compassAngle >= 345 || compassAngle < 15) baseName = "Straight Hit / Bowler";
    else if (compassAngle >= 15 && compassAngle < 45) baseName = isRightHandedBat ? "Mid Off" : "Mid On"; // Top Right
    else if (compassAngle >= 45 && compassAngle < 80) baseName = isRightHandedBat ? "Cover" : "Mid Wicket";
    else if (compassAngle >= 80 && compassAngle < 100) baseName = isRightHandedBat ? "Point" : "Square Leg";
    else if (compassAngle >= 100 && compassAngle < 135) baseName = isRightHandedBat ? "Gully" : "Backward Square";
    else if (compassAngle >= 135 && compassAngle < 165) baseName = isRightHandedBat ? "Third Man" : "Fine Leg";
    else if (compassAngle >= 165 && compassAngle < 195) baseName = isRightHandedBat ? "Wicket Keeper / Slip" : "Wicket Keeper / Slip"; // Behind stumps
    else if (compassAngle >= 195 && compassAngle < 225) baseName = isRightHandedBat ? "Fine Leg" : "Third Man";
    else if (compassAngle >= 225 && compassAngle < 260) baseName = isRightHandedBat ? "Backward Square" : "Gully";
    else if (compassAngle >= 260 && compassAngle < 280) baseName = isRightHandedBat ? "Square Leg" : "Point";
    else if (compassAngle >= 280 && compassAngle < 315) baseName = isRightHandedBat ? "Mid Wicket" : "Cover";
    else if (compassAngle >= 315 && compassAngle < 345) baseName = isRightHandedBat ? "Mid On" : "Mid Off";

    // 4. Refine based on Distance
    if (distance < CLOSE_CATCHING_RADIUS) {
        if (baseName.includes("Slip")) return isRightHandedBat ? "1st Slip" : "Leg Slip";
        if (baseName.includes("Gully")) return "Gully"; // Close gully is just gully
        if (baseName.includes("Point")) return "Silly Point";
        if (baseName.includes("Mid Off")) return "Silly Mid Off";
        if (baseName.includes("Mid On")) return "Silly Mid On";
        if (baseName.includes("Square Leg")) return "Short Leg";
        return "Short " + baseName;
    }

    if (prefix === 'Deep') {
        if (baseName === "Mid Wicket") return "Deep Mid Wicket";
        if (baseName === "Square Leg") return "Deep Square Leg";
        if (baseName === "Point") return "Deep Point"; // Or Sweeper
        if (baseName === "Cover") return "Deep Cover"; // Or Sweeper
        if (baseName === "Mid Off") return "Long Off";
        if (baseName === "Mid On") return "Long On";
        if (baseName === "Fine Leg") return "Fine Leg"; // Usually deep by default unless "Short"
        if (baseName === "Third Man") return "Third Man"; // Usually deep
        if (baseName === "Backward Square") return "Deep Backward Square";
    }

    // Default Circle positions
    return baseName;
};

/**
 * Mirrors field coordinates horizontally for RHB <-> LHB switching.
 * @param {Array} fielders - Array of field objects {x, y, ...}
 * @returns {Array} - New array with mirrored X coordinates
 */
export const mirrorCoordinates = (fielders) => {
    return fielders.map(f => ({
        ...f,
        x: 100 - f.x
    }));
};

export const getZoneColor = (zoneName) => {
    if (zoneName.includes('Deep') || zoneName.includes('Long')) return 'rgba(255, 0, 0, 0.2)'; // High run potential
    if (zoneName.includes('Slip') || zoneName.includes('Gully')) return 'rgba(255, 255, 0, 0.2)'; // Catches
    return 'transparent';
};
